import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ProfileTab } from "enums/ProfileTabs";

import { useActivityItemsFromSOWWithStacks } from "services/input-core/activity-items/get-activity-item-from-sow-adm";
import { useSOWQuery } from "services/input-core/sows/get-sow-by-id";
import { useGetSOWsByIdSowAdminQuery } from "services/input-core/sows/get-sow-operative-by-sow-adm-id";

import GridContainer from "components/UI/Grid/GridContainer";
import GridItemTS from "components/UI/Grid/GridItemTS";
import { TabContent, TabSystem } from "components/UI/TabSystem";
import Header from "./components/Header";
import SOWsRelated from "./components/SOWsRelated/SOWsRelated";

import GeneralTab, { BuildDetailsSection } from "./tabs/GeneralTab/GeneralTab";
import ActivityItemsTab from "./tabs/ActivityItemsTab/ActivityItemsTab";

import {
  parseDateAndTime,
  tryRetrieveDateFromUrlParams,
} from "helpers/DateHelper";
import { toast } from "react-toastify";
import {
  Line,
  StyledParagraph,
  StyledParagraphValue,
} from "./tabs/GeneralTab/GeneralTab.styles";

import { useActivityItemContext } from "store/activityItem";
import { useSowAdmContext } from "store/sowADM";

const SowAdmProfile: React.FC = () => {
  const { t } = useTranslation("sow-profile");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sowAdmId = searchParams.get("id");
  const {getSowProfile, sowProfile}= useSowAdmContext()

  const { data, isLoading } = useSOWQuery(sowAdmId, "admin", {
    enabled: !!sowAdmId,
    onSuccess: (data: any) => {
      getSowProfile(data);
      if (data?.status === "Closed")
        toast.error(t("info-sow-inactive", { ns: "list-messages" }));
    },
    onError: (error: any) => {
      if (error?.response?.data?.error === "Invalid sow template: admin") {
        toast.error(t("danger-sow-template-admin", { ns: "list-messages" }));
        navigate("/app/sows/profile?id=" + sowAdmId);
        return;
      }

      if (error?.response?.data?.statusCode === 404) {
        navigate("*");
        toast.error(t("danger-sow-not-found", { ns: "list-messages" }));
        return;
      }

      if (error?.response?.data?.statusCode === 403) {
        navigate("/app/access-denied");
        toast.error(t("access-denied-message", { ns: "list-messages" }));
        return;
      }
    },
  });

  const [date, setDate] = useState<Date>(() => tryRetrieveDateFromUrlParams());

  const {
    setActivityItems,
    setLoading: setLoadingActivity,
    setRefetching: setRefetchingActivity,
  } = useActivityItemContext();

  const {
    data: activityItemsQuery,
    isLoading: isActivityItemsLoadingQuery,
    isRefetching: isActivityItemsRefetchingQuery,
    isInitialLoading: isActivityItemsInitialLoading,
    refetch: refetchActivityItems,
  } = useActivityItemsFromSOWWithStacks(data?.sowID, date, {
    enabled: !isLoading,
  });

  const { data: sows, isLoading: isLoadingSows } = useGetSOWsByIdSowAdminQuery(
    data?.sowID,
    {
      enabled: !isLoading && !!sowAdmId,
    },
  );

  useEffect(() => {
    setLoadingActivity(isActivityItemsLoadingQuery);
    setRefetchingActivity(isActivityItemsRefetchingQuery);
    if (activityItemsQuery) setActivityItems(activityItemsQuery);
  }, [
    activityItemsQuery,
    isActivityItemsLoadingQuery,
    isActivityItemsRefetchingQuery,
    setActivityItems,
    setLoadingActivity,
    setRefetchingActivity,
  ]);

  const tabs = [
    {
      id: ProfileTab.GENERAL,
      title: t("General", { ns: "profile" }),
    },
    {
      id: ProfileTab.ACTIVITY_ITEMS,
      title: t("Activity Items", { ns: "profile" }),
    },
    {
      id: ProfileTab.SOWS_RELATED,
      title: t("Sows Related", { ns: "profile" }),
    },
  ];

  const sowBuildDetails: BuildDetailsSection[] = [
    {
      title: t("SOW Details", { ns: "profile" }),
      items: [
        {
          text: t("SOW ID", { ns: "profile" }),
          value: data?.sowID,
        },
        {
          text: t("SOW Name", { ns: "profile" }),
          value: data?.sowName,
        },
        {
          text: t("Sow Owner", { ns: "profile" }),
          value: data?.sowOwner,
        },
        {
          text: t("Sow Creator", { ns: "profile" }),
          value: data?.sowCreator,
        },
        {
          text: t("Supplier", { ns: "profile" }),
          value: data?.supplierName,
        },
        {
          text: t("Primary Contact Person", { ns: "profile" }),
          value: data?.supplier?.supplierPrimaryContactPerson,
        },
      ],
    },
    {
      title: t("Secondary Details", { ns: "profile" }),
      items: [
        {
          text: t("Billable", { ns: "profile" }),
          value: t(data?.billable),
        },
        {
          text: t("Created At", { ns: "profile" }),
          value: parseDateAndTime(data?.createdAt),
        },
        {
          text: t("Updated At", { ns: "profile" }),
          value: parseDateAndTime(data?.updatedAt),
        },
      ],
    },
    {
      title: t("Custom Fields", { ns: "profile" }),
      items: [
        {
          text: t("Fields for Uverify", { ns: "profile" }),
          value: (
            <div>
              {data?.customFields?.map(
                (
                  field: {
                    name: string;
                    value: any;
                  },
                  index: number,
                ) => (
                  <Line key={index}>
                    <StyledParagraph>
                      {t(field?.name, { ns: "custom-fields" })}:
                    </StyledParagraph>
                    {typeof field.value === "object" ? (
                      <StyledParagraphValue>
                        {JSON.stringify(field.value)}
                      </StyledParagraphValue>
                    ) : (
                      <StyledParagraphValue>
                        {t(field.value, { ns: "profile" })}
                      </StyledParagraphValue>
                    )}
                  </Line>
                ),
              )}
            </div>
          ),
        },
      ],
    },
  ];

  return (
    <GridContainer>
      <GridItemTS xs={12}>
        <Header
          data={{
            fieldglassId: data?.sowID,
            name: data?.sowName,
            status: data?.status,
            zIdentifier: data?.zId,
            startDate: data?.startDate,
            endDate: data?.endDate,
            supplierName: data?.supplierName,
            supplierCode: data?.supplierCode,
          }}
          isLoading={isLoading}
          type="sow"
        />
      </GridItemTS>

      <GridItemTS xs={12} style={{ marginTop: "1rem" }}>
        <TabSystem tabs={tabs} syncWithUrlParams>
          <TabContent value={ProfileTab.GENERAL}>
            <GeneralTab isLoading={isLoading} buildDetails={sowBuildDetails} />
          </TabContent>

          <TabContent value={ProfileTab.ACTIVITY_ITEMS}>
            <ActivityItemsTab
              fieldglassId={data?.sowID}
              entityDate={{ start: data?.startDate, end: data?.endDate }}
              date={date}
              setDate={setDate}
              isActivityItemsInitialLoading={isActivityItemsInitialLoading}
              refetchActivityItems={refetchActivityItems}
              profile={"SOW"}
              supplierCode={data?.supplierCode}
            />
          </TabContent>

          <TabContent value={ProfileTab.SOWS_RELATED}>
            <SOWsRelated
              sows={sows ?? []}
              isLoading={isLoadingSows}
              isSowAdm={true}
            />
          </TabContent>
        </TabSystem>
      </GridItemTS>
    </GridContainer>
  );
};

export default SowAdmProfile;
